import { Dialog } from "@headlessui/react";
import { IconCircleXFilled, IconMail, IconX } from "@tabler/icons-react";
import { Controller, useForm } from "react-hook-form";
import Button from "./Button";
import toast from "react-hot-toast";
import { connect, useDispatch } from "react-redux";
import { sendEmail } from "../store/contract/actions";
import { isScopeLoading } from "../store/scope-loading/helpers";
import { RootState } from "../store/root-reducer";
import { ContractScope } from "../store/contract/types";

interface ClaimFileSendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerEmail: string;
  customerPhone?: string;
  planId: string;
  contactId: string;
  loading: boolean;
}

function ClaimFileSendEmailModel({
  isOpen,
  onClose,
  customerEmail,
  customerPhone,
  planId,
  contactId,
  loading,
}: ClaimFileSendEmailModalProps) {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: customerEmail || "",
      phone: customerPhone || "",
      confirmEmail: false,
      confirmPhone: false,
    },
  });
  const emailValue = watch("email");
  const emailRegex = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const emailValid = emailRegex.test(emailValue);

  const confirmChecked = watch("confirmEmail");
  const confirmPhoneChecked = watch("confirmPhone");
  const phoneValue = watch("phone");

  const dispatch = useDispatch();
  const onSubmit = (data: any) => {
    const oldEmail = customerEmail;
    const newEmail = data.email;

    const payload = {
      planId,
      contactId,
      oldEmail,
      newEmail,
      phone: data.phone,
      oldPhone: customerPhone,
    };

    dispatch(
      sendEmail(
        {
          data: payload,
        },
        () => {
          onClose();
          toast.success("Email has been send");
          reset({
            email: "",
            confirmEmail: false,
          });
        }
      )
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-email-title"
          className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 gap-3 px-4 py-3 text-gray-800">
            <h2
              id="send-email-title"
              className="text-base sm:text-lg font-medium flex items-center gap-2"
            >
              <IconMail size={20} className="text-slate-500" />
              Send Link
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <IconX />
            </button>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4 p-4 sm:p-6 flex-1 overflow-y-auto text-sm text-gray-700"
          >
            <div className="space-y-2">
              <div>
                <label className="block mb-1 font-medium">Customer Email</label>
                <Controller
                  control={control}
                  name="email"
                  rules={{
                    pattern: {
                      value:
                        /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Please enter a valid email",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <div className="relative">
                        <input
                          {...field}
                          type="email"
                          className={`w-full appearance-none border read-only:bg-gray-100 border-gray-300 px-3 py-2.5 pr-10 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 invalid:border-1 invalid:border-red-500 focus:outline-none focus:shadow-none focus:ring-0 text-sm ${
                            error
                              ? "border-red-500 focus:border-red-500"
                              : watch("confirmEmail")
                                ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                                : "border-gray-300"
                          }`}
                          readOnly={watch("confirmEmail")}
                        />
                        {field.value && !watch("confirmEmail") && (
                          <button
                            type="button"
                            onClick={() => {
                              setValue("email", "");
                              setValue("confirmEmail", false);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <IconCircleXFilled size={18} />
                          </button>
                        )}
                      </div>
                      {error && (
                        <p className="mt-1 text-red-500 text-xs">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="confirmEmail"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      id="confirmEmail"
                      className="accent-blue-500 cursor-pointer"
                      checked={field.value}
                      disabled={!emailValid}
                      onChange={(e) => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  )}
                />
                <label htmlFor="confirmEmail" className="text-sm">
                  I have confirmed the customer’s email.
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <label className="block mb-1 font-medium">Customer Phone</label>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <div className="relative">
                      <input
                        {...field}
                        type="text"
                        className={`w-full appearance-none border read-only:bg-gray-100 border-gray-300 px-3 py-2.5 pr-10 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:shadow-none focus:ring-0 text-sm ${
                          watch("confirmPhone")
                            ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-gray-300"
                        }`}
                        readOnly={watch("confirmPhone")}
                      />
                      {field.value && !watch("confirmPhone") && (
                        <button
                          type="button"
                          onClick={() => {
                            setValue("phone", "");
                            setValue("confirmPhone", false);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <IconCircleXFilled size={18} />
                        </button>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="confirmPhone"
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      id="confirmPhone"
                      className="accent-blue-500 cursor-pointer"
                      checked={field.value}
                      disabled={!phoneValue}
                      onChange={(e) => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  )}
                />
                <label htmlFor="confirmPhone" className="text-sm">
                  I have confirmed the customer’s phone number.
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 mt-auto">
              <Button variant="blue" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="blue"
                type="submit"
                disabled={
                  (!emailValue && !phoneValue) ||
                  (!!emailValue && (!emailValid || !confirmChecked)) ||
                  (!!phoneValue && !confirmPhoneChecked) ||
                  loading
                }
              >
                {loading ? "Sending..." : "Send Link"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}

const mapStateToProps = (state: RootState) => ({
  loading: isScopeLoading(state.scopeLoading, `contact-${ContractScope}`),
});

export default connect(mapStateToProps)(ClaimFileSendEmailModel);
